import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { magicOtt } from '@/api/generated/auth/auth';
import { AuthActionsContext, AuthContext } from '@/shared/auth/AuthContext';
import { useNavigator } from '@/shared/navigation/react/useNavigator';
import { useNavigationContext } from '@/shared/navigation/react/useNavigationContext';
import type { StoredAgendaConfig } from '@/shared/navigation/core';
import { legacyFormAgenda } from '@/modules/legacy-form/legacyFormAgenda';
import { LegacyFormShellPage } from '@/modules/legacy-form/LegacyFormShellPage';
import { consumePendingLegacyNavSource, registerMagicControlFrame } from '@/shared/lib/legacyMagic';
import { recordAgendaHistory, linkLayerToHistory } from '@/shared/lib/agendaHistory';
import {
  categoryOf,
  hasObjectCategoryNav,
  openObjectCategoryDetail,
  openObjectCategoryList,
} from '@/shared/lib/objectCategoryNav';
import { buildMagicControlUrl, getMagicBaseUrl, setMagicResourceRevision } from './magicUrl';
import { consumePendingMenuOpen } from './pendingMenuOpen';
import { useSyncLegacyDarkMode } from './darkMode';
import { useSyncLegacyCulture } from './culture';
import { useMagicMenuStore } from './magicMenuStore';
import { useMagicDesktopStore } from './magicDesktopStore';
import {
  MAGIC_TO_PHOENIX_ACTIONS,
  PHOENIX_TO_MAGIC_ACTIONS,
  CLAIM_REQUEST_ID_PARAM,
  type DesktopMenuReadyData,
  type DesktopToolFlags,
  type DesktopUserInfoData,
  type FormOpenData,
} from './types';
import { legacyFormSourceRegistry } from './legacyFormSourceRegistry';
import {
  MagicControlBridgeContext,
  type MagicControlBridgeContextValue,
} from './MagicControlBridgeContext';

/** Fallback, když bridge běží bez AuthProvideru (izolované testy). */
const noopReauth = async (): Promise<void> => {};

/**
 * Pojistka proti zacyklení Phoenix-On-Top auto-renew: kolikrát po sobě smí iframe poslat
 * `auth/required` (= redeem selhal) bez úspěšného `desktop/menu-ready`, než to vzdáme a zobrazíme
 * chybu místo věčného načítání plochy. Počítadlo se nuluje při úspěšném menu-ready / odhlášení.
 */
const MAX_PHOENIX_AUTH_RETRIES = 3;

/** Okno (ms), v němž se `auth/required` počítají jako jeden „rychlý sled" (loop). Odstup je nuluje. */
const PHOENIX_AUTH_LOOP_WINDOW_MS = 8000;

/**
 * Časový limit (ms) na první `desktop/menu-ready` od chvíle, kdy se control iframe začne načítat.
 * Když Magic plocha do limitu nic nepošle — typicky Magic neběží / vrátí 500 / chybovou stránku,
 * která žádné postMessage neposílá — přestaneme točit spinner a v liště zobrazíme chybu.
 */
const MAGIC_MENU_LOAD_TIMEOUT_MS = 15000;

/**
 * Vytáhne id záznamu z `params` legacy `form/open` (pro reroute na native detail). Zkouší běžné
 * klíče `ID`/`Id`/`id`; přijme číslo i numerický string. Vrací `null` při nejistotě → volající
 * spadne na legacy formulář. POZN: přesná konvence klíče je legacy strana — ověřit za běhu (plán, Phase C).
 */
function readLegacyFormId(params: Record<string, unknown> | undefined): string | number | null {
  if (!params) return null;
  for (const key of ['ID', 'Id', 'id']) {
    const value = params[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

interface MagicControlBridgeProps {
  readonly children: ReactNode;
}

/**
 * Skrytý iframe na Magic control instanci. Mountuje se v rootu Phoenixu, když je aktivní
 * Phoenix-On-Top režim (viz [phoenixOnTopConfig.ts](./phoenixOnTopConfig.ts)).
 *
 * Pipeline:
 * - Magic control instance po načtení pošle `desktop/menu-ready` se strukturou menu →
 *   ukládáme do [magicMenuStore](./magicMenuStore.ts), MainMenu si ho čte.
 * - Uživatel kliká v Phoenix MainMenu → komponenta volá `sendMenuItemClick(menuItemId)` →
 *   Magic interně vyvolá akci (form/click) → ta probublá zpět jako `form/open`.
 * - Magic pošle `form/open { globalName, params }` → otevřeme novou instanci
 *   [legacyFormAgenda]( $args[0].Value -replace 'features/', 'modules/' legacy-form/legacyFormAgenda.ts) jako modal nad
 *   aktuálním stackem.
 */
export function MagicControlBridge({ children }: MagicControlBridgeProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const setItems = useMagicMenuStore((s) => s.setItems);
  const resetMenu = useMagicMenuStore((s) => s.reset);
  const setMenuLoadError = useMagicMenuStore((s) => s.setLoadError);
  const isMenuReady = useMagicMenuStore((s) => s.isReady);
  const setUserInfo = useMagicDesktopStore((s) => s.setUserInfo);
  const setToolFlags = useMagicDesktopStore((s) => s.setToolFlags);
  const resetDesktop = useMagicDesktopStore((s) => s.reset);
  const { openModal, closeLayer } = useNavigator();
  const { kernel } = useNavigationContext();
  // Auth čteme přes useContext (ne useAuth/useAuthActions), aby šel bridge mountnout i izolovaně
  // v testech bez AuthProvideru. V reálné app je AuthProvider vždy přítomen.
  const authState = useContext(AuthContext);
  const authActions = useContext(AuthActionsContext);
  const isAuthenticated = authState?.status === 'authenticated';
  const reauthenticate = authActions?.reauthenticate ?? noopReauth;

  const baseUrl = useMemo(() => getMagicBaseUrl(), []);
  // URL control iframu se staví ASYNCHRONNĚ: nejdřív vyrobíme OTT (/api/auth/magic-ott), pak teprve
  // nastavíme src (Magic ho na serveru zredeemuje a založí si FormsAuth cookie). Dokud OTT nemáme,
  // iframe nemontujeme (jinak by skončil na LoginForm).
  const [controlUrl, setControlUrl] = useState<string | null>(null);
  // Bumpne se při runtime přepnutí jazyka → vynutí remount (a tím reload) control iframu i tehdy,
  // když výsledná URL vyjde stejná jako minule (návrat na dříve použitý jazyk).
  const [controlReloadKey, setControlReloadKey] = useState(0);
  const mintingRef = useRef(false);
  // Počítadlo neúspěšných auth/required cyklů (loop-breaker) + čas posledního pokusu (okno).
  const phoenixAuthRetryRef = useRef(0);
  const phoenixAuthWindowRef = useRef(0);

  // Po přihlášení (a jen jednou) vyrobíme OTT a sestavíme URL control iframu.
  useEffect(() => {
    if (!baseUrl || !isAuthenticated || controlUrl !== null || mintingRef.current) return;
    mintingRef.current = true;
    let cancelled = false;
    void (async () => {
      try {
        const { token } = await magicOtt();
        if (!cancelled) setControlUrl(buildMagicControlUrl(token));
      } catch {
        // Mince selhalo (typicky vypršelá Phoenix session) → přehodnotit stav → LoginGate.
        if (!cancelled) void reauthenticate();
      } finally {
        mintingRef.current = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl, isAuthenticated, controlUrl, reauthenticate]);

  // Pojistka proti věčnému spinneru: jakmile se control iframe začne načítat (máme `controlUrl`)
  // a ještě nedorazilo `desktop/menu-ready`, naběhne časový limit. Když do něj plocha nic nepošle
  // (Magic neběží / 500 / chybová stránka), zobrazíme v liště chybu místo donekonečna točeného
  // kolečka. Úspěšné menu-ready (isMenuReady) limit zruší a `setItems` navíc smaže případnou chybu.
  useEffect(() => {
    if (!controlUrl || isMenuReady) return;
    const timer = window.setTimeout(() => setMenuLoadError(true), MAGIC_MENU_LOAD_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [controlUrl, isMenuReady, setMenuLoadError]);

  // Při odhlášení / ztrátě session iframe odmontujeme a vyčistíme desktop+menu store.
  useEffect(() => {
    if (isAuthenticated) return;
    setControlUrl(null);
    phoenixAuthRetryRef.current = 0;
    resetMenu();
    resetDesktop();
    registerMagicControlFrame(null);
  }, [isAuthenticated, resetMenu, resetDesktop]);

  // Runtime přepnutí dark/light propíše do control iframu (load-time stav nese URL param).
  useSyncLegacyDarkMode(() => iframeRef.current?.contentWindow ?? null);

  // Runtime přepnutí jazyka Phoenixu → reload skrytého control iframu (Magic v ApplyUserCulture novou
  // kulturu uloží a znovu pošle `desktop/menu-ready` v daném jazyce → menu store a tím Phoenix MainMenu
  // se obnoví). OTT NEMINTUJEME: když jsme přihlášení, má Magic platnou FormsAuth cookie a reload ji
  // využije (OTT není potřeba). URL přestavíme bez OTT, jen s aktuálním `culture`; remount přes
  // `controlReloadKey` zajistí reload VŽDY (i při návratu na dříve použitý jazyk, kdy by URL vyšla
  // stejná). Kdyby Magic session chyběla, naběhne existující auth/required tok.
  const reloadControlForCulture = useCallback(() => {
    if (!isAuthenticated) return;
    setControlUrl(buildMagicControlUrl());
    setControlReloadKey((k) => k + 1);
  }, [isAuthenticated]);
  useSyncLegacyCulture(reloadControlForCulture);

  // Legacy-form agendu registrujeme globálně, ne až ve své SlicePage. Důvod: bridge ji
  // chce otevřít přes openModal nad libovolnou aktuální agendou (Landing, Columbus, ...).
  // Bez globální registrace by openModal selhal s "agenda nemá přiřazenou komponentu".
  useEffect(() => {
    const registry = kernel.getRegistry();
    if (!registry.has(legacyFormAgenda.id)) {
      kernel.registerAgenda(legacyFormAgenda.config as StoredAgendaConfig);
    }
    kernel.registerComponent(legacyFormAgenda.id, LegacyFormShellPage);
  }, [kernel]);

  // Track source windows pro relay výsledků: po `form/open` z konkrétního iframu si pamatujeme
  // event.source pod jeho requestId — když pak target iframe pošle `form/result { requestId, result }`,
  // víme, komu výsledek postnout zpátky jako `form/result-relayed`.
  const sourceByRequestIdRef = useRef<Map<string, Window>>(new Map());

  useEffect(() => {
    function handle(event: MessageEvent) {
      const raw = event.data as { action?: string; data?: unknown } | null | undefined;
      if (!raw || typeof raw.action !== 'string') return;

      switch (raw.action) {
        case MAGIC_TO_PHOENIX_ACTIONS.desktopMenuReady: {
          // Plocha úspěšně naběhla → vynuluj loop-breaker (setItems navíc smaže případnou chybu).
          phoenixAuthRetryRef.current = 0;
          const payload = raw.data as DesktopMenuReadyData | undefined;
          if (payload && Array.isArray(payload.items)) {
            setItems(payload.items);
          }
          // Sdílený resource revision z hlavního Magic iframu → použije se pro URL oken legacy agend.
          if (payload && typeof payload.revision === 'string') {
            setMagicResourceRevision(payload.revision);
          }
          break;
        }
        case MAGIC_TO_PHOENIX_ACTIONS.desktopUserInfo: {
          const payload = raw.data as DesktopUserInfoData | undefined;
          if (payload) setUserInfo(payload);
          break;
        }
        case MAGIC_TO_PHOENIX_ACTIONS.desktopToolsReady: {
          const payload = raw.data as DesktopToolFlags | undefined;
          if (payload) setToolFlags(payload);
          break;
        }
        case MAGIC_TO_PHOENIX_ACTIONS.authRequired: {
          // Magic iframe (control / single-form) ztratil Magic session → vyrobíme čerstvý OTT
          // a pošleme ho zpět ZDROJI zprávy; ten se s ním sám reloadne (Magic redeem → cookie).
          // Když mince selže (mrtvá Phoenix session) → přehodnotit stav → LoginGate.
          const source = event.source as Window | null;
          if (!source) break;
          // POJISTKA proti zacyklení: pokud redeem opakovaně selhává (cookie se nepropíše), přestaneme
          // re-mintovat a místo věčného načítání plochy zobrazíme chybu v liště (badge „Magic2G").
          // Počítáme jen RYCHLÝ sled (loop); větší odstup = legitimní re-auth → počítadlo vynulujeme.
          const now = Date.now();
          if (now - phoenixAuthWindowRef.current > PHOENIX_AUTH_LOOP_WINDOW_MS) {
            phoenixAuthRetryRef.current = 0;
          }
          phoenixAuthWindowRef.current = now;
          if (phoenixAuthRetryRef.current >= MAX_PHOENIX_AUTH_RETRIES) {
            setMenuLoadError(true);
            break;
          }
          phoenixAuthRetryRef.current += 1;
          void magicOtt()
            .then(({ token }) => {
              source.postMessage(
                { action: PHOENIX_TO_MAGIC_ACTIONS.authOtt, data: { ott: token } },
                '*'
              );
            })
            .catch(() => {
              void reauthenticate();
            });
          break;
        }
        case MAGIC_TO_PHOENIX_ACTIONS.formOpen: {
          const payload = raw.data as FormOpenData | undefined;
          if (!payload || typeof payload.globalName !== 'string') return;

          const source = event.source as Window | null;

          // Pokud Magic chce výsledek (claim navigace), uložíme zdroj a propagujeme requestId
          // přes formParams — Magic single-form v novém iframu si ho zase vyzvedne z URL.
          const requestId = payload.requestId;
          if (requestId && source) sourceByRequestIdRef.current.set(requestId, source);

          const formParams: Record<string, unknown> = { ...(payload.params ?? {}) };
          if (requestId) formParams[CLAIM_REQUEST_ID_PARAM] = requestId;

          // Navigace z OSAMOSTATNĚNÉ (floating) agendy musí novou agendu otevřít ve STEJNÉM stacku,
          // ne na ploše. Zdrojovou vrstvu poznáme dvěma cestami:
          //   1) navigace ZEVNITŘ legacy formuláře → zdroj zprávy je iframe okna → layerId přes registr,
          //   2) navigace z NATIVNÍ Phoenix UI (⋯/proklik v selektoru, tlačítko v tabulce, `onOpen`
          //      ListPage) → jde přes SKRYTÝ control iframe, který okno neidentifikuje; odesílatel
          //      proto svůj layerId uložil bokem (viz `navSource`) a my si ho tu vyzvedneme.
          // Z plochy/menu zdroj neexistuje (`null`) → nová vrstva zůstane na ploše.
          // POZOR: resolveLayerId vrací u NEregistrovaného okna (control iframe = menu) `undefined`,
          // ne `null` → normalizujeme na `null`, jinak by `sourceLayerId === null` níže bylo false
          // a záměr z menu (replace/detached) by se nikdy nespotřeboval (otevíralo by se jako modal).
          const frameLayerId = source
            ? (legacyFormSourceRegistry.resolveLayerId(source) ?? null)
            : null;
          // Vyzvedni VŽDY (i když má přednost zdroj z iframu) — jinak by zapomenutý záznam přetekl
          // do některé z dalších navigací.
          const phoenixLayerId = consumePendingLegacyNavSource();
          const sourceLayerId = frameLayerId ?? phoenixLayerId;
          const sourceStackId = sourceLayerId ? kernel.getLayer(sourceLayerId)?.stackId : undefined;

          // Postupná náhrada legacy → phoenix: navigace ZE VRSTVY (sourceLayerId != null — legacy
          // formulář i native UI), jejíž ObjectCategory má zaregistrovanou native agendu, přesměruj na native list/detail místo
          // hostování legacy formuláře. Bez jediné změny na straně legacy. Podmínky:
          //  - claim navigace (requestId) přeskočíme — ta čeká výsledek zpět v Magicu (legacy picker),
          //  - z menu/plochy (sourceLayerId === null) nererouteujeme — tam volbu native/legacy řeší
          //    úroveň menu (native položka nahrazuje legacy přes replacesKey).
          if (sourceLayerId !== null && !requestId) {
            const objectCategory = categoryOf(payload.globalName);
            if (hasObjectCategoryNav(objectCategory)) {
              if (/\.list$/i.test(payload.globalName)) {
                if (openObjectCategoryList(objectCategory, sourceLayerId)) break;
              } else {
                const nativeId = readLegacyFormId(payload.params);
                if (
                  nativeId != null &&
                  openObjectCategoryDetail(objectCategory, nativeId, sourceLayerId)
                ) {
                  break;
                }
              }
            }
          }

          // Záměr z menu (replace/detached) platí jen pro open Z MENU — tj. zdroj NENÍ registrované
          // agenda-okno (control iframe / neznámý). Navigace ZEVNITŘ agendy (sourceLayerId != null)
          // záměr nemá → běžný modal + dědění stacku.
          const pending = sourceLayerId === null ? consumePendingMenuOpen() : null;

          // JAKÁKOLIV legacy navigace (z menu i zevnitř agendy) → zapiš do historie. Identita položky
          // je GlobalName + VŠECHNY parametry (viz agendaHistory.makeId), takže různé záznamy téže
          // agendy s jinými parametry jsou různé položky.
          // Zápis do historie zatím jen s identifikátorem (GlobalName / menu titulek). Po načtení okna
          // se titulek doplní z window/chrome (viz useLegacyWindowChrome → updateAgendaHistoryTitleForLayer)
          // — proto navážeme nový layerId na tento záznam přes linkLayerToHistory.
          const historyId = recordAgendaHistory({
            kind: 'legacy',
            target: payload.globalName,
            title: pending?.title ?? payload.globalName,
            params: payload.params,
          });

          if (pending?.mode === 'replace') {
            void kernel
              .openRoot(legacyFormAgenda, { formGlobalName: payload.globalName, formParams })
              .then(() => {
                const newId = kernel.getState().activeTopLayerId;
                if (newId) linkLayerToHistory(newId, historyId);
              });
            break;
          }
          if (pending?.mode === 'detached') {
            void openModal(legacyFormAgenda, {
              formGlobalName: payload.globalName,
              formParams,
            }).then(() => {
              const newId = kernel.getState().activeTopLayerId;
              if (newId) {
                // Fresh stackId → projekce zobrazí okno jako floating (PhoenixDesktop naseeduje rect).
                kernel.setLayerStackId(newId, `s_${crypto.randomUUID()}`);
                linkLayerToHistory(newId, historyId);
              }
            });
            break;
          }

          void openModal(legacyFormAgenda, {
            formGlobalName: payload.globalName,
            formParams,
          }).then(() => {
            const newId = kernel.getState().activeTopLayerId;
            if (!newId) return;
            linkLayerToHistory(newId, historyId);
            if (sourceStackId) kernel.setLayerStackId(newId, sourceStackId);
          });
          break;
        }
        case MAGIC_TO_PHOENIX_ACTIONS.formResult: {
          // Magic single-form se zavřel.
          // POZN: `instanceof Window` neuděláme — v cross-origin scénáři je `event.source`
          // WindowProxy z jiného realmu a check by selhal. WeakMap lookup s WindowProxy
          // jako klíčem funguje (browser garantuje stejnou referenci).
          const source = event.source as Window | null;
          const data = (raw.data ?? {}) as { result?: unknown; requestId?: string };

          // Pokud má result requestId, relayujeme ho zpět source iframu, který si claim navigaci
          // zaregistroval (např. SelectorBrowseButton.OnClosed dostane vybrané ID).
          if (data.requestId) {
            const sourceWindow = sourceByRequestIdRef.current.get(data.requestId);
            sourceByRequestIdRef.current.delete(data.requestId);
            if (sourceWindow) {
              sourceWindow.postMessage(
                {
                  action: PHOENIX_TO_MAGIC_ACTIONS.formResultRelayed,
                  data: {
                    requestId: data.requestId,
                    result: typeof data.result === 'string' ? data.result : null,
                  },
                  requestId: crypto.randomUUID(),
                },
                '*'
              );
            }
          }

          // V každém případě zavřeme přidruženou Phoenix agenda.
          if (source) {
            const layerId = legacyFormSourceRegistry.resolveLayerId(source);
            if (layerId) void closeLayer(layerId);
          }
          break;
        }
      }
    }

    window.addEventListener('message', handle);
    return () => {
      window.removeEventListener('message', handle);
      resetMenu();
      resetDesktop();
    };
  }, [
    setItems,
    resetMenu,
    setMenuLoadError,
    setUserInfo,
    setToolFlags,
    resetDesktop,
    openModal,
    closeLayer,
    kernel,
    reauthenticate,
  ]);

  // Po načtení control iframu ho zapíšeme do globální utility, ať ho `getMagicDesktopFrame()`
  // dokáže najít z libovolného místa (typicky `useLegacyMessaging` v `shared/lib/legacyMagic`).
  // Na unmount registraci vyčistíme.
  const handleIframeLoad = useCallback(() => {
    registerMagicControlFrame(iframeRef.current?.contentWindow ?? null);
  }, []);

  useEffect(() => {
    return () => {
      registerMagicControlFrame(null);
    };
  }, []);

  const send = useCallback((action: string, data?: Record<string, unknown>) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      { action, data: data ?? {}, requestId: crypto.randomUUID() },
      '*'
    );
  }, []);

  const sendMenuItemClick = useCallback(
    (menuItemId: string) => {
      send(PHOENIX_TO_MAGIC_ACTIONS.desktopClickMenuItem, { menuItemId });
    },
    [send]
  );

  const sendCloseToLegacyForm = useCallback((layerId: string) => {
    const target = legacyFormSourceRegistry.resolveWindow(layerId);
    if (!target) return;
    target.postMessage(
      {
        action: PHOENIX_TO_MAGIC_ACTIONS.formCloseWindow,
        data: {},
        requestId: crypto.randomUUID(),
      },
      '*'
    );
  }, []);

  const logoutMagic = useCallback((logoutUrl: string): Promise<void> => {
    // Magic SignOut provedeme přes JEDNORÁZOVÝ sandboxovaný iframe (ne přes control iframe).
    // `sandbox` BEZ `allow-scripts` = LoginForm.aspx (kam Magic po SignOut redirectuje) NESPUSTÍ svůj
    // frame-buster (`self.parent.location = self.location`), který by jinak vykopl celý Phoenix
    // top-frame do LoginFormu. `allow-same-origin` nechá request nést Magic cookie, takže server-side
    // SignOut proběhne a cookie se smaže. Po načtení (nebo timeoutu) iframe zahodíme.
    return new Promise<void>((resolve) => {
      const frame = document.createElement('iframe');
      frame.setAttribute('sandbox', 'allow-same-origin');
      frame.setAttribute('aria-hidden', 'true');
      frame.style.display = 'none';

      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        frame.remove();
        resolve();
      };
      // Pojistka: kdyby `load` nedorazil (cross-origin redirect apod.), odhlášení neblokuj.
      const timer = window.setTimeout(finish, 3000);
      frame.addEventListener('load', finish);

      frame.src = logoutUrl;
      document.body.appendChild(frame);
    });
  }, []);

  const value = useMemo<MagicControlBridgeContextValue>(
    () => ({ sendMenuItemClick, send, sendCloseToLegacyForm, logoutMagic }),
    [sendMenuItemClick, send, sendCloseToLegacyForm, logoutMagic]
  );

  if (!baseUrl) {
    // Konfigurace chybí — provider se nemonteruje. Komponenty pod ním by neměly volat
    // useMagicControlBridge bez ošetření; v praxi `isPhoenixOnTopMode()` strážíme dřív.
    return <>{children}</>;
  }

  return (
    <MagicControlBridgeContext.Provider value={value}>
      {controlUrl ? (
        <iframe
          key={controlReloadKey}
          ref={iframeRef}
          title="magic-control"
          src={controlUrl}
          data-testid="magic-control-iframe"
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            border: 0,
            visibility: 'hidden',
          }}
          aria-hidden="true"
          onLoad={handleIframeLoad}
        />
      ) : null}
      {children}
    </MagicControlBridgeContext.Provider>
  );
}
